/**
 * Permission Utility Functions
 * Task 5.8: Update Permission Checks in Frontend
 */

import type { TeamRole, TeamMemberPermissions } from '@/types/teamMember.types';

/**
 * Get permissions for a given role
 */
export function getRolePermissions(role: TeamRole): TeamMemberPermissions {
  return {
    canEditCelebration: role === 'organizer',
    canDeleteCelebration: role === 'organizer',
    canManageTeam: role === 'organizer',
    canManageEvents: role === 'organizer' || role === 'event_manager',
    canManageGuests: role === 'organizer' || role === 'event_manager',
    canAddRemoveGuests: role === 'organizer' || role === 'event_manager' || role === 'host',
    canManageVendors: role === 'organizer' || role === 'vendor_coordinator',
    canManageServices: role === 'organizer' || role === 'vendor_coordinator',
    canManageActivities: role === 'organizer' || role === 'event_manager',
    canCreateTasks: role !== 'member' && role !== 'host',
    canViewAll: true
  };
}

/**
 * Check if a role has permission for a specific action
 */
export function hasPermission(
  role: TeamRole | null | undefined,
  permission: keyof TeamMemberPermissions
): boolean {
  if (!role) return false;
  const permissions = getRolePermissions(role);
  return permissions[permission];
}

/**
 * Check if user can edit celebration
 */
export function canEditCelebration(role: TeamRole | null | undefined): boolean {
  return hasPermission(role, 'canEditCelebration');
}

/**
 * Check if user can delete celebration
 */
export function canDeleteCelebration(role: TeamRole | null | undefined): boolean {
  return hasPermission(role, 'canDeleteCelebration');
}

/**
 * Check if user can manage team
 */
export function canManageTeam(role: TeamRole | null | undefined): boolean {
  return hasPermission(role, 'canManageTeam');
}

/**
 * Check if user can manage events
 */
export function canManageEvents(role: TeamRole | null | undefined): boolean {
  return hasPermission(role, 'canManageEvents');
}

/**
 * Check if user can manage guests
 */
export function canManageGuests(role: TeamRole | null | undefined): boolean {
  return hasPermission(role, 'canManageGuests');
}

export function canAddRemoveGuests(role: TeamRole | null | undefined): boolean {
  return hasPermission(role, 'canAddRemoveGuests');
}

/**
 * Check if user can manage vendors
 */
export function canManageVendors(role: TeamRole | null | undefined): boolean {
  return hasPermission(role, 'canManageVendors');
}

/**
 * Check if user can manage services
 */
export function canManageServices(role: TeamRole | null | undefined): boolean {
  return hasPermission(role, 'canManageServices');
}

/**
 * Check if user can manage activities
 */
export function canManageActivities(role: TeamRole | null | undefined): boolean {
  return hasPermission(role, 'canManageActivities');
}

/**
 * Check if user can create tasks
 */
export function canCreateTasks(role: TeamRole | null | undefined): boolean {
  return hasPermission(role, 'canCreateTasks');
}

/**
 * Role hierarchy check - does userRole have permission for requiredRole
 */
export function checkRolePermission(userRole: TeamRole, requiredRole: TeamRole): boolean {
  const roleHierarchy: Record<TeamRole, TeamRole[]> = {
    organizer: ['organizer', 'event_manager', 'host', 'vendor_coordinator', 'member'],
    event_manager: ['event_manager', 'host', 'member'],
    host: ['host', 'member'],
    vendor_coordinator: ['vendor_coordinator', 'member'],
    member: ['member']
  };

  return roleHierarchy[userRole]?.includes(requiredRole) || false;
}
